const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// VAPID public key - Esta se genera en el backend
// Por ahora usamos una placeholder, luego la obtendremos del servidor
let vapidPublicKey = null;

/**
 * Convierte la VAPID key de base64 a Uint8Array
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Verificar si el navegador soporta Push Notifications
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
};

/**
 * Verificar el estado actual del permiso
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', 'default'
};

/**
 * Solicitar permiso para notificaciones
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Este navegador no soporta notificaciones');
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  console.log('Permiso de notificaciones:', permission);
  return permission;
};

/**
 * Obtener la VAPID public key del servidor
 */
export const getVapidPublicKey = async () => {
  if (vapidPublicKey) return vapidPublicKey;

  try {
    const response = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
    if (response.ok) {
      const data = await response.json();
      vapidPublicKey = data.publicKey;
      return vapidPublicKey;
    }
  } catch (error) {
    console.error('Error obteniendo VAPID key:', error);
  }
  return null;
};

/**
 * Suscribir al usuario a push notifications
 */
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) {
    console.log('Push notifications no soportadas');
    return null;
  }

  try {
    // Obtener el service worker registrado
    const registration = await navigator.serviceWorker.ready;

    // Obtener VAPID key del servidor
    const publicKey = await getVapidPublicKey();
    if (!publicKey) {
      console.log('No se pudo obtener VAPID key');
      return null;
    }

    // Verificar si ya existe una suscripción
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Crear nueva suscripción
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      console.log('Nueva suscripción push creada');
    }

    // Enviar suscripción al servidor
    const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subscription: subscription.toJSON()
      })
    });

    if (response.ok) {
      console.log('Suscripción guardada en servidor');
      localStorage.setItem('push_subscribed', 'true');
      return subscription;
    }
  } catch (error) {
    console.error('Error suscribiendo a push:', error);
  }

  return null;
};

/**
 * Cancelar suscripción a push notifications
 */
export const unsubscribeFromPush = async (token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Cancelar en el navegador
      await subscription.unsubscribe();

      // Notificar al servidor
      await fetch(`${API_URL}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });

      localStorage.removeItem('push_subscribed');
      console.log('Suscripción cancelada');
      return true;
    }
  } catch (error) {
    console.error('Error cancelando suscripción:', error);
  }
  return false;
};

/**
 * Verificar si el usuario está suscrito
 */
export const isSubscribedToPush = async () => {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
};

/**
 * Mostrar notificación local (sin push del servidor)
 */
export const showLocalNotification = async (title, options = {}) => {
  if (getNotificationPermission() !== 'granted') {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/icons/notification-icon.png',
      badge: '/icons/logdevos.png',
      vibrate: [200, 100, 200],
      ...options
    });
    return true;
  } catch (error) {
    console.error('Error mostrando notificación:', error);
    return false;
  }
};

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  showLocalNotification
};
